import { useState } from 'react';
import { AlertTriangle, Trash2, Download, Archive, CreditCard } from 'lucide-react';
import Modal from '../ui/Modal';
import { useTranslation } from '../../context/LanguageContext';
import {
    consequencesSuppression, peutSupprimer, motifEchec
} from '../../utils/suppressionCompte';

/**
 * La confirmation d'une suppression de compte.
 *
 * REMPLACE UN window.confirm portant le seul nom de la personne. Pour un
 * geste irréversible qui efface des années de contenu et résilie des
 * abonnements payants, c'était trop peu — et rien n'annonçait ce qui
 * serait CONSERVÉ.
 *
 * Trois choses sont dites avant d'agir : ce qui part, ce qui reste, ce qui
 * s'arrête. Taire le deuxième serait malhonnête — la personne croirait tout
 * effacé alors qu'une pièce comptable subsiste, rattachée à son numéro
 * client. Le RGPD l'autorise (art. 17.3.b) à condition de l'annoncer.
 *
 * DEUX VERROUS : retaper son adresse, et reconnaître avoir lu. Un seul se
 * franchit par habitude.
 */
const SuppressionCompteModal = ({
    isOpen,
    onClose,
    onConfirm,
    email,
    apercu,
    enCours
}) => {
    const { t } = useTranslation();
    const s = t.suppressionCompte || {};

    const [saisie, setSaisie] = useState('');
    const [lu, setLu] = useState(false);
    const [echec, setEchec] = useState('');

    const conseq = consequencesSuppression({
        aUnAbonnement: apercu?.aUnAbonnement,
        aDesFactures: apercu?.aDesFactures
    });

    const autorise = peutSupprimer({ saisie, email, consequencesLues: lu });

    const fermer = () => {
        // Repartir de zéro : rouvrir la modale ne doit pas retrouver une
        // confirmation déjà saisie.
        setSaisie('');
        setLu(false);
        setEchec('');
        onClose();
    };

    const confirmer = async () => {
        setEchec('');
        try {
            await onConfirm();
        } catch (err) {
            // Le statut porte le sens : 402 dit que Mollie a refusé et que
            // le compte est INTACT — un message d'échec générique laisserait
            // croire à une suppression partielle.
            setEchec(motifEchec(err?.status ?? err?.statusCode));
        }
    };

    const Section = ({ icone: Icone, titre, couleur, children }) => (
        <div className={`rounded-xl border p-4 ${couleur}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icone className="w-4 h-4 flex-shrink-0" />
                <span className="font-semibold text-sm">{titre}</span>
            </div>
            <ul className="space-y-1 ml-6 list-disc text-sm">{children}</ul>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={fermer}
            title={s.titre}
            size="md"
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={fermer}
                        disabled={enCours}
                        className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-gray-300 transition-colors disabled:opacity-50"
                    >
                        {s.annuler}
                    </button>
                    <button
                        type="button"
                        onClick={confirmer}
                        disabled={!autorise || enCours}
                        className="px-5 py-2.5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        <Trash2 className="w-4 h-4" />
                        {s.supprimer}
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-semibold text-red-900">{s.irreversible}</p>
                </div>

                <Section
                    icone={Trash2}
                    titre={s.ceQuiPart}
                    couleur="bg-gray-50 border-gray-200 text-gray-700"
                >
                    {conseq.effacé.map((cle) => (
                        <li key={cle}>{s[cle]}</li>
                    ))}
                </Section>

                {/* Annoncer ce qui reste importe autant que ce qui part : sans
                    cela, la personne croirait tout effacé. */}
                {conseq.conservé.length > 0 && (
                    <Section
                        icone={Archive}
                        titre={s.ceQuiReste}
                        couleur="bg-blue-50 border-blue-200 text-blue-900"
                    >
                        {conseq.conservé.map((cle) => (
                            <li key={cle}>{s[cle]}</li>
                        ))}
                    </Section>
                )}

                {conseq.arrêté.length > 0 && (
                    <Section
                        icone={CreditCard}
                        titre={s.ceQuiStoppe}
                        couleur="bg-amber-50 border-amber-200 text-amber-900"
                    >
                        {conseq.arrêté.map((cle) => (
                            <li key={cle}>{s[cle]}</li>
                        ))}
                    </Section>
                )}

                {/* L'export se propose ICI, au seul moment où la personne en a
                    besoin : après, ses données n'existeront plus. */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-purple-50 border border-purple-100">
                    <Download className="w-4 h-4 text-cabaza-purple flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-semibold text-purple-900">{s.exporter}</div>
                        <div className="text-purple-700 text-xs mt-0.5">{s.exporterAide}</div>
                    </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={lu}
                        onChange={(e) => setLu(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-cabaza-purple focus:ring-cabaza-purple"
                    />
                    <span className="text-sm text-gray-700">{s.jaiLu}</span>
                </label>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {s.confirmer}
                    </label>
                    <input
                        type="email"
                        value={saisie}
                        onChange={(e) => setSaisie(e.target.value)}
                        placeholder={email}
                        autoComplete="off"
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-400 transition-colors"
                    />
                </div>

                {echec && (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-900">
                        {s[echec]}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SuppressionCompteModal;
